import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import { useAuthStore } from "./store/useAuthStore";
import { useFormStore } from "./store/useFormStore";
import { useThemeStore } from "./store/useThemeStore";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageSkeleton } from "./components/ui/PageSkeleton";

import LoginView from "./components/views/LoginView";
import { AdminView } from "./components/views/AdminView";
import { ManagementView } from "./components/views/ManagementView";
import { CustomFlowView } from "./components/views/CustomFlowView";
import ConfirmationView from "./components/views/ConfirmationView";
import DeceasedConfirmationView from "./components/views/DeceasedConfirmationView";
import SolicitarRecalculoView from "./components/views/SolicitarRecalculoView";
import { SindicatoView } from "./components/views/SindicatoView";
import MaintenanceView from "./components/views/MaintenanceView";

import { Header } from "./components/layouts/Header";
import { AdminLayout } from "./components/layouts/AdminLayout";
import { Toaster } from "sonner";

import { mapDataToForm } from "./lib/utils";
import bancos from "./lib/bancos.json";
import { userService } from "./services/user.service";
import { useSessionTimeout } from "./hooks/useSessionTimeout";

// Lazy-loaded admin views
const DashboardView = React.lazy(() =>
  import("./components/views/admin/DashboardView").then(m => ({ default: m.DashboardView }))
);
const AssinaturasView = React.lazy(() =>
  import("./components/views/admin/AssinaturasView").then(m => ({ default: m.AssinaturasView }))
);
const TemaView = React.lazy(() =>
  import("./components/views/admin/TemaView").then(m => ({ default: m.TemaView }))
);
const RolesView = React.lazy(() => import("./components/views/admin/RolesView"));
const UsuariosView = React.lazy(() => import("./components/views/admin/UsuariosView"));
const SindicatosView = React.lazy(() => import("./components/views/admin/SindicatosView"));
const DocumentTypesView = React.lazy(() => import("./components/views/admin/DocumentTypesView"));
const DocsView = React.lazy(() =>
  import("./components/views/admin/DocsView").then(m => ({ default: m.DocsView }))
);
const WebhookLogsView = React.lazy(() => import("./components/views/admin/WebhookLogsView"));
const CRMView = React.lazy(() =>
  import("./components/views/admin/CRMView").then(m => ({ default: m.CRMView }))
);
const DataConnectionsView = React.lazy(() =>
  import("./components/views/admin/DataConnectionsView").then(m => ({ default: m.DataConnectionsView }))
);

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

function ProtectedRoute({ children, allowedRoles = [], reqAdmin = false, reqSindicato = false }) {
  const { isAuthenticated, isAdminMode, isSindicatoMode, permissions, skipPortalRedirect } = useAuthStore();

  if (!isAuthenticated) {
    if (PORTAL_URL && !skipPortalRedirect) {
      window.location.replace(PORTAL_URL);
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // Fine-grained permission check
  if (allowedRoles.length > 0 && !allowedRoles.some(r => permissions.includes(r))) {
    return <Navigate to="/form" replace />;
  }

  if (reqAdmin && !isAdminMode) return <Navigate to="/login" replace />;
  if (reqSindicato && !isSindicatoMode) return <Navigate to="/login" replace />;

  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const { isLoggingIn, login, logout, isAdminMode, isSindicatoMode, sindicatoId, cpf: storedCpf, isAuthenticated } = useAuthStore();
  const loadTheme = useThemeStore((s) => s.loadTheme);
  const maintenanceMode = useThemeStore((s) => !!s.theme?.maintenance_mode);

  useEffect(() => {
    loadTheme(sindicatoId, { skipSlug: isAdminMode && isAuthenticated });
  }, [loadTheme, sindicatoId, isAdminMode, isAuthenticated]);

  const {
    userData,
    bancoData,
    setUserData,
    setBancoData,
    isSubmitting,
    handleUserDataChange,
    handleBancoChange,
    submitForm,
    selectedProcessIds,
    setSelectedProcessIds,
    clearForm,
    initializeProcesses,
  } = useFormStore();

  const handleLoginSuccess = (data, cpfInput, tipo) => {
    const mapped = mapDataToForm(data, cpfInput);
    const bank = bancos.find((b) => b.nome === data.banco);
    const newBancoData = {
      nomeBanco: bank ? `${bank.codigo} - ${data.banco}` : data.banco || "",
      agencia: data.agencia || "",
      conta: data.conta_corrente
        ? `${data.conta_corrente}-${data.digito_conta}`
        : "",
      tipoPix: data.tipo_pix || "cpf",
      chavePix: data.pix || "",
      tipoConta: data.tipo_conta || "corrente",
    };
    setUserData(mapped);
    setBancoData(newBancoData);
    navigate(tipo ? `/form?tipo=${tipo}` : "/form");
  };

  // Re-hidrata userData do cedente após refresh de página.
  // isAuthenticated persiste, mas useFormStore não — se cedente atualiza a página, userData é null.
  useEffect(() => {
    if (!isAuthenticated || isAdminMode || isSindicatoMode) return;
    if (userData || !storedCpf) return;
    userService.getUserByCpf(storedCpf).then((data) => {
      handleLoginSuccess(data, storedCpf, null);
    }).catch(() => {
      // CPF não encontrado ou bloqueado — faz logout para limpar estado inválido
      logout();
      navigate("/login");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const onLogin = async (identifier, extra, dataNascimento = null) => {
    const res = await login(identifier, extra, dataNascimento);
    if (res?.success) {
      if (res.role === "admin") navigate("/admin");
      else if (res.role === "sindicato") navigate("/sindicato");
      else handleLoginSuccess(res.data, res.cpf, extra);
    }
    return res;
  };

  const onAdminUserSelectSuccess = (data) => {
    const mapped = mapDataToForm(data);
    const bank = bancos.find((b) => b.nome === data.banco);
    const newBancoData = {
      nomeBanco: bank ? `${bank.codigo} - ${data.banco}` : data.banco || "",
      agencia: data.agencia || "",
      conta: data.conta_corrente
        ? `${data.conta_corrente}-${data.digito_conta}`
        : "",
      tipoPix: data.tipo_pix || "cpf",
      chavePix: data.pix || "",
      tipoConta: data.tipo_conta || "corrente",
    };
    setUserData(mapped);
    setBancoData(newBancoData);
    navigate("/form");
  };

  const handleReturnToAdmin = () => {
    clearForm();
    navigate("/admin/cedentes");
  };

  const handleLogout = () => {
    clearForm();
    logout();
    navigate("/login");
  };

  useSessionTimeout({
    onLogout: handleLogout,
    enabled: isAuthenticated && !isAdminMode && !isSindicatoMode,
  });

  const handleSubmitWrapper = async (tipoAceite, isFalecido) => {
    const success = await submitForm(tipoAceite, isFalecido);
    return success;
  };

  const adminBypass = window.location.pathname === "/login"
    && new URLSearchParams(window.location.search).get("admin") === "1";
  if (maintenanceMode && !(isAuthenticated && isAdminMode) && !adminBypass) {
    return <MaintenanceView />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* ── Standalone public pages (sem Header/Footer) ── */}
          <Route path="/:processoId/envio-documentos" element={<SolicitarRecalculoView />} />

      {/* ── Public / user-facing routes (with Header + footer) ── */}
      <Route
        path="/*"
        element={
          <div className="min-h-screen bg-background flex flex-col">
            <Header
              isLoggedIn={isAuthenticated}
              onLogout={handleLogout}
            />
            <main className="flex flex-col flex-grow items-center justify-center">
              <Routes>

                <Route
                  path="/login"
                  element={<LoginView onLogin={onLogin} isLoading={isLoggingIn} />}
                />

                <Route
                  path="/sindicato"
                  element={
                    <ProtectedRoute reqSindicato={true}>
                      <SindicatoView />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/form"
                  element={
                    <ProtectedRoute>
                      <CustomFlowView
                        userData={userData}
                        bancoData={bancoData}
                        selectedProcessIds={selectedProcessIds}
                        setSelectedProcessIds={setSelectedProcessIds}
                        isSubmitting={isSubmitting}
                        handleUserDataChange={handleUserDataChange}
                        handleBancoChange={handleBancoChange}
                        handleSubmit={handleSubmitWrapper}
                        isAdminMode={isAdminMode}
                        onReturnToAdmin={handleReturnToAdmin}
                        onSubmitSuccess={() => navigate("/confirmation")}
                        initializeProcesses={initializeProcesses}
                      />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/confirmation"
                  element={
                    <ProtectedRoute>
                      {userData?.falecido ? (
                        <DeceasedConfirmationView
                          userData={userData}
                          isAdminMode={isAdminMode}
                          onReturnToAdmin={handleReturnToAdmin}
                          onExit={handleLogout}
                        />
                      ) : (
                        <ConfirmationView
                          userData={userData}
                          isAdminMode={isAdminMode}
                          onReturnToAdmin={handleReturnToAdmin}
                          onExit={handleLogout}
                        />
                      )}
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </main>
            <footer
              className="text-center p-4 text-sm border-t border-border"
              style={{ backgroundColor: "var(--footer-bg, #ffffff)", color: "var(--footer-fg, #64748b)" }}
            >
              © 2026 Juri Ativos Judiciais. Todos os direitos reservados.
            </footer>
          </div>
        }
      />

      {/* ── Admin routes (with AdminLayout sidebar) ── */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Dashboard">
              <DashboardView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/cedentes"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Substituídos">
              <AdminView onUserSelect={onAdminUserSelectSuccess} />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/management"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Gestão">
              <ManagementView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/assinaturas"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Provedores de Assinatura">
              <AssinaturasView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/tema"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Personalização de Tema">
              <TemaView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Perfis e Permissões">
              <RolesView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Usuários do Sistema">
              <UsuariosView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sindicatos"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Sindicatos">
              <SindicatosView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/document-types"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Tipos de Documento">
              <DocumentTypesView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/docs"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Documentacao">
              <DocsView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webhooks"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Webhook Logs">
              <WebhookLogsView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/crm"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Integracoes CRM">
              <CRMView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-connections"
        element={
          <ProtectedRoute reqAdmin={true}>
            <AdminLayout pageTitle="Conexoes de Dados">
              <DataConnectionsView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <Toaster position="top-right" richColors closeButton style={{ zIndex: 99999 }} />
    </BrowserRouter>
  );
}

export default App;
